import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import {
  getAdminOverview,
  getAdminEmailMetrics,
  type AdminOverview,
  type EmailMetrics,
} from '../../lib/api/admin';
import {
  RANGE_PRESETS,
  PRESET_LABELS,
  isRangePreset,
  resolveRange,
  isBoundedRange,
  type RangePreset,
} from '../../lib/adminRange';
import { isoToLocalInput } from '../../lib/datetimeLocal';
import { FilterField } from './FilterField';
import { GameplayCharts } from './GameplayCharts';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: AdminOverview };

type EmailState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: EmailMetrics };

const nf = new Intl.NumberFormat('en-US');
const fmt = (n: number): string => nf.format(n);
const pct = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

/**
 * Overview dashboard tab. Account + gameplay aggregates from
 * GET /admin/stats/overview (DEV_USER excluded; page traffic lives in
 * Plausible, not here). Read-only. Each metric group is its own card.
 *
 * A date-range selector (GHS5-90) scopes the filterable cards. The chosen range
 * lives in the URL (`?range=`, plus `?from=&to=` for custom) so it round-trips
 * across reload / shared links. Running-total and current-state cards can't
 * honor a range and carry an explicit "All-time" badge instead of being
 * silently wrong.
 */
const EMAIL_WINDOW_DAYS = 30;

// Gameplay card readouts (GHS5-99). The table is the range-filtered view that
// has always been here; the graph works in whole promotion weeks instead.
const PLAY_VIEWS = [
  { id: 'table', label: 'Table' },
  { id: 'graph', label: 'Graph' },
] as const;
type PlayView = (typeof PLAY_VIEWS)[number]['id'];

export function OverviewTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const preset: RangePreset = isRangePreset(rangeParam) ? rangeParam : 'all';
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';
  // In the URL like the range, so a reload or a shared link lands on the same
  // readout. 'table' is the default and carries no param.
  const playView: PlayView = searchParams.get('play') === 'graph' ? 'graph' : 'table';

  const [state, setState] = useState<State>({ kind: 'loading' });
  // Email metrics come from CloudWatch via a separate endpoint, loaded
  // independently so a slow/failed metrics read never blocks the overview.
  const [email, setEmail] = useState<EmailState>({ kind: 'loading' });

  // Range is resolved INSIDE the effects (keyed on the stable selection
  // primitives, not the resolved instants) so a preset re-samples "now" at
  // fetch time and the deps don't churn every render.
  useEffect(() => {
    const controller = new AbortController();
    const range = resolveRange(preset, customFrom, customTo);
    getAdminOverview(
      { fromDate: range.fromDate, toDate: range.toDate },
      { signal: controller.signal },
    )
      .then((data) => setState({ kind: 'ready', data }))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState({
          kind: 'error',
          message:
            err instanceof ApiError
              ? `Couldn't load stats (${err.status}).`
              : "Couldn't load stats. Please try again.",
        });
      });
    return () => controller.abort();
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    const controller = new AbortController();
    const range = resolveRange(preset, customFrom, customTo);
    // The email endpoint needs BOTH bounds to honor a range; otherwise fall back
    // to its rolling 30-day window (the all-time selection has no bounds).
    const req = isBoundedRange(range)
      ? getAdminEmailMetrics(
          { fromDate: range.fromDate, toDate: range.toDate },
          { signal: controller.signal },
        )
      : getAdminEmailMetrics({ window: EMAIL_WINDOW_DAYS }, { signal: controller.signal });
    req
      .then((data) => setEmail({ kind: 'ready', data }))
      .catch(() => {
        if (controller.signal.aborted) return;
        setEmail({ kind: 'error' });
      });
    return () => controller.abort();
  }, [preset, customFrom, customTo]);

  // What the from/to inputs display: the custom values verbatim, or the current
  // preset's resolved window (so the applied window stays visible). Empty for
  // "All time".
  const preview = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo };
    const r = resolveRange(preset, '', '');
    return { from: isoToLocalInput(r.fromDate), to: isoToLocalInput(r.toDate) };
  }, [preset, customFrom, customTo]);

  const selectPreset = (p: RangePreset): void => {
    const next = new URLSearchParams(searchParams);
    next.set('range', p);
    if (p === 'custom') {
      // Seed the inputs from the window currently shown so switching to Custom
      // doesn't blank the range.
      if (preview.from) next.set('from', preview.from);
      else next.delete('from');
      if (preview.to) next.set('to', preview.to);
      else next.delete('to');
    } else {
      next.delete('from');
      next.delete('to');
    }
    setSearchParams(next, { replace: true });
  };

  const editCustom = (which: 'from' | 'to', value: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('range', 'custom');
    // Editing either input flips to Custom; preserve the other bound, seeding it
    // from the preset window we were just showing if it wasn't set yet.
    const from = which === 'from' ? value : (searchParams.get('from') ?? preview.from);
    const to = which === 'to' ? value : (searchParams.get('to') ?? preview.to);
    if (from) next.set('from', from);
    else next.delete('from');
    if (to) next.set('to', to);
    else next.delete('to');
    setSearchParams(next, { replace: true });
  };

  const selectPlayView = (id: PlayView): void => {
    const next = new URLSearchParams(searchParams);
    if (id === 'graph') next.set('play', 'graph');
    else next.delete('play');
    setSearchParams(next, { replace: true });
  };

  const rangeActive = preset !== 'all';
  const emailBounded = isBoundedRange(resolveRange(preset, customFrom, customTo));

  return (
    <div className="flex flex-col gap-6">
      <RangeSelector
        preset={preset}
        from={preview.from}
        to={preview.to}
        onPreset={selectPreset}
        onEdit={editCustom}
      />

      {state.kind === 'loading' ? (
        <p className="text-white/60">Loading stats…</p>
      ) : state.kind === 'error' ? (
        <p className="text-white/80">{state.message}</p>
      ) : (
        <Cards
          data={state.data}
          email={email}
          rangeActive={rangeActive}
          emailBounded={emailBounded}
          playView={playView}
          onPlayView={selectPlayView}
        />
      )}
    </div>
  );
}

function RangeSelector({
  preset,
  from,
  to,
  onPreset,
  onEdit,
}: {
  preset: RangePreset;
  from: string;
  to: string;
  onPreset: (p: RangePreset) => void;
  onEdit: (which: 'from' | 'to', value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-white/55">Range</span>
        {RANGE_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={p === preset}
            onClick={() => onPreset(p)}
            className={[
              'rounded-md border px-3 py-1.5 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
              p === preset
                ? 'border-brand-redAccent bg-brand-redAccent/20 text-white'
                : 'border-white/15 text-white/75 hover:bg-white/10',
            ].join(' ')}
          >
            {PRESET_LABELS[p]}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="From">
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => onEdit('from', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => onEdit('to', e.target.value)}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          />
        </FilterField>
      </div>
    </div>
  );
}

function Cards({
  data,
  email,
  rangeActive,
  emailBounded,
  playView,
  onPlayView,
}: {
  data: AdminOverview;
  email: EmailState;
  rangeActive: boolean;
  emailBounded: boolean;
  playView: PlayView;
  onPlayView: (id: PlayView) => void;
}) {
  const { users, consent, play, economy, shop } = data;
  const allTimeBadge = rangeActive ? <AllTimeBadge /> : undefined;
  const emailTitle = emailBounded
    ? 'Email delivery (selected range)'
    : `Email delivery (last ${EMAIL_WINDOW_DAYS} days)`;

  return (
    <>
      <Card title="Accounts">
        <StatGrid>
          <Stat label="Total users" value={fmt(users.total)} />
          <Stat label="Active" value={fmt(users.active)} />
          <Stat label="Pending" value={fmt(users.pending)} sub="Started, not finished" />
          <Stat label="Suspended" value={fmt(users.suspended)} />
          <Stat label="Disabled" value={fmt(users.disabled)} />
          <Stat label="Dev users" value={fmt(users.devUsers)} sub="Excluded from all totals" />
        </StatGrid>
      </Card>

      <Card title="Consent & email">
        <StatGrid>
          <Stat
            label="Marketing opt-in"
            value={pct(consent.marketingOptInRate)}
            sub={`${fmt(consent.marketingOptIns)} of ${fmt(consent.activeUsers)} active`}
          />
          <Stat
            label="Episode reminders"
            value={pct(consent.reminderOptInRate)}
            sub={`${fmt(consent.reminderOptIns)} of ${fmt(consent.activeUsers)} active`}
          />
          <Stat
            label="Undeliverable email"
            value={fmt(consent.emailUndeliverable)}
            sub="Bounced / complained"
          />
        </StatGrid>
      </Card>

      <Card title={emailTitle}>
        <EmailMetricsTable state={email} />
      </Card>

      <Card title="Gameplay">
        <StatGrid>
          <Stat label="Total runs" value={fmt(play.totalRuns)} />
          <Stat label="Unique players" value={fmt(play.uniquePlayers)} sub="Played at least once" />
        </StatGrid>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-heading text-xs uppercase tracking-wide text-white/55">By week</h4>
          <div className="flex items-center gap-1 rounded-md border border-white/15 p-0.5">
            {PLAY_VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                aria-pressed={v.id === playView}
                onClick={() => onPlayView(v.id)}
                className={[
                  'rounded px-3 py-1 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                  v.id === playView ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white',
                ].join(' ')}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table honors the date range; the graph works in whole promotion
            weeks and says so. Mounted lazily so its fetch only fires when an
            operator actually opens it. */}
        {playView === 'graph' ? (
          <div className="mt-4">
            <GameplayCharts />
          </div>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/55">
                  <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">Week</th>
                  <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">Runs</th>
                  <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">Players</th>
                  <th className="px-3 py-2 font-heading text-xs uppercase tracking-wide">
                    Top score
                    {rangeActive && (
                      <span className="ml-1 normal-case tracking-normal text-white/35">(all-time)</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {play.byWeek.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-3 text-white/50">
                      No weeks scheduled.
                    </td>
                  </tr>
                ) : (
                  play.byWeek.map((w) => (
                    <tr key={w.weekNumber} className="border-b border-white/5 last:border-0 text-white/85">
                      <td className="px-3 py-2">Week {w.weekNumber}</td>
                      <td className="px-3 py-2">{fmt(w.runs)}</td>
                      <td className="px-3 py-2">{fmt(w.players)}</td>
                      <td className="px-3 py-2">{fmt(w.topScore)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Coin economy" badge={allTimeBadge}>
        <StatGrid>
          <Stat label="Total earned" value={fmt(economy.totalEarned)} />
          <Stat label="Total spent" value={fmt(economy.totalSpent)} />
          <Stat label="Outstanding balance" value={fmt(economy.outstandingFloat)} sub="Unspent coins" />
        </StatGrid>
      </Card>

      <Card title="Top Stats Purchased" badge={allTimeBadge}>
        {shop.topStats.length === 0 ? (
          <p className="text-white/50">No stats purchased yet.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {shop.topStats.map((s, i) => (
              <li key={s.statId} className="flex items-center justify-between text-sm text-white/85">
                <span>
                  <span className="text-white/45">{i + 1}.</span> {s.name}
                </span>
                <span className="font-heading">{fmt(s.owners)}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  );
}

function EmailMetricsTable({ state }: { state: EmailState }) {
  if (state.kind === 'loading') return <p className="text-white/50">Loading email metrics…</p>;
  if (state.kind === 'error') return <p className="text-white/50">Couldn&rsquo;t load email metrics.</p>;
  if (!state.data.metricsAvailable) {
    return <p className="text-white/50">Email metrics are temporarily unavailable.</p>;
  }

  const cols = ['Type', 'Sent', 'Delivered', 'Bounced', 'Bounce rate', 'Opened', 'Open rate', 'Clicked', 'Click rate'];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 text-white/55">
            {cols.map((c) => (
              <th key={c} className="px-3 py-2 font-heading text-xs uppercase tracking-wide">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state.data.types.map((t) => (
            <tr key={t.value} className="border-b border-white/5 last:border-0 text-white/85">
              <td className="px-3 py-2">{t.label}</td>
              <td className="px-3 py-2">{fmt(t.sent)}</td>
              <td className="px-3 py-2">{fmt(t.delivered)}</td>
              <td className="px-3 py-2">{fmt(t.bounced)}</td>
              <td className="px-3 py-2">{pct(t.bounceRate)}</td>
              <td className="px-3 py-2">{fmt(t.opened)}</td>
              <td className="px-3 py-2">{pct(t.openRate)}</td>
              <td className="px-3 py-2">{fmt(t.clicked)}</td>
              <td className="px-3 py-2">{pct(t.clickRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-[11px] text-white/40">
        From CloudWatch (SES event metrics). &ldquo;Untagged&rdquo; is mail sent before per-type
        tagging; opens are approximate (image-proxy inflation).
      </p>
    </div>
  );
}

function Card({ title, badge, children }: { title: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-sm uppercase tracking-wide text-white/70">{title}</h3>
        {badge}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function AllTimeBadge() {
  return (
    <span
      title="This metric is a running / current-state total and can't be scoped to a date range."
      className="shrink-0 rounded-full border border-white/20 bg-white/5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/50"
    >
      All-time
    </span>
  );
}

function StatGrid({ children }: { children: ReactNode }) {
  // auto-fit so cells stretch to fill the card width at any count — avoids
  // a fixed column track leaving 2–3-stat cards lopsidedly left-aligned.
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">{children}</div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md bg-white/5 p-4">
      <div className="text-2xl font-semibold text-white">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-white/55">{label}</div>
      {sub && <div className="mt-1 text-[11px] text-white/40">{sub}</div>}
    </div>
  );
}
